import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, GraduationCap, Award, Briefcase } from "lucide-react";
import { LOGO_URL, BRAND } from "../lib/brand";
import { useAuth } from "../contexts/AuthContext";
import { PortalNavButton } from "../components/PortalNavButton";

const CALENDLY_URL = "https://calendly.com/ramzybaroody";
const PORTRAIT_URL = "/ramzy-baroody.jpg";

const CREDENTIALS = [
  { icon: GraduationCap, label: "Ph.D. in Higher Education Leadership" },
  { icon: Award, label: "MBA" },
  { icon: GraduationCap, label: "M.S. — Information Technology & Operations Management" },
  { icon: GraduationCap, label: "M.Ed. — Educational Leadership" },
  { icon: Briefcase, label: "Project Management Professional (PMP)" },
];

const EXPERTISE = [
  "Business IT Tools",
  "Business Systems & Processes",
  "Excel for Business",
  "Project Management",
  "Business Analytics",
  "AI Integration in Higher Education",
  "Curriculum Design",
  "Learning Management Systems",
  "Microsoft Project",
];

function AboutNav({ isAuthed }) {
  return (
    <nav data-testid="about-nav" className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#0b1d3a]/85 border-b border-[#d4af37]/20">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between gap-6">
        <Link to="/" className="flex items-center gap-3" data-testid="about-logo-link">
          <img src={LOGO_URL} alt="CFCS" className="h-9 w-auto rounded-sm" />
        </Link>
        <div className="hidden md:flex items-center gap-7 text-sm text-[#fff8e1]/75">
          <Link to="/" className="hover:text-[#d4af37] transition-colors" data-testid="about-nav-home">Home</Link>
          <Link to="/catalog" className="hover:text-[#d4af37] transition-colors" data-testid="about-nav-catalog">Course Catalog</Link>
          <Link to="/about" className="text-[#d4af37] font-semibold" data-testid="about-nav-about">About</Link>
        </div>
        <div className="flex items-center gap-3">
          {isAuthed ? (
            <Link to="/dashboard" data-testid="about-nav-dashboard" className="text-sm text-[#d4af37] hover:underline">Open dashboard →</Link>
          ) : (
            <PortalNavButton testId="about-nav-signin" />
          )}
          <a href={CALENDLY_URL} target="_blank" rel="noreferrer" data-testid="about-nav-cta" className="hidden sm:inline-flex items-center gap-1.5 bg-[#d4af37] text-[#0b1d3a] px-4 py-2 rounded-md text-sm font-semibold hover:bg-[#e2bd44] transition-colors">
            Book walkthrough <ArrowRight className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>
    </nav>
  );
}

export default function About() {
  const { user } = useAuth();

  return (
    <div data-testid="about-page" className="min-h-screen bg-[#0b1d3a] text-[#fff8e1]">
      <AboutNav isAuthed={!!user} />

      {/* Hero */}
      <section className="pt-32 pb-16 px-6 lg:px-10">
        <div className="max-w-6xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#fff8e1]/60 hover:text-[#d4af37] transition-colors mb-8" data-testid="about-back-link">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>

          <div className="grid md:grid-cols-[minmax(0,1fr)_1.5fr] gap-10 md:gap-14 items-start">
            {/* Portrait */}
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden border-2 border-[#d4af37]/40 shadow-[0_0_60px_rgba(212,175,55,0.15)] bg-[#11264c]">
                <img
                  src={PORTRAIT_URL}
                  alt="Dr. Ramzy Baroody, Director of Training"
                  className="w-full h-auto block"
                  data-testid="about-portrait"
                />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0b1d3a] via-[#0b1d3a]/70 to-transparent p-5 pt-16">
                  <p className="font-display text-xl font-bold text-[#fff8e1] leading-tight">Dr. Ramzy Baroody</p>
                  <p className="text-xs uppercase tracking-[0.15em] text-[#d4af37] mt-1">Founder & Director of Training</p>
                </div>
              </div>
            </div>

            {/* Copy */}
            <div>
              <p className="text-xs tracking-[0.25em] uppercase text-[#d4af37] mb-4 font-semibold">About the Instructor</p>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-[#fff8e1] leading-[1.1]">
                Practical training,<br />
                <span className="text-[#d4af37]">taught by a Ph.D. Professor.</span>
              </h1>
              <p className="mt-6 text-lg text-[#fff8e1]/80 leading-relaxed">
                Central Florida Corporate Solutions was founded and led by <strong className="text-[#fff8e1]">Dr. Ramzy Baroody</strong>, who also serves as Director of Training. A business technology educator, researcher, and Project Management Professional dedicated to turning technology, data, and business processes into practical tools for better decision-making, efficiency, and performance.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Credentials */}
      <section className="py-16 md:py-20 px-6 lg:px-10 bg-[#0a1830] border-y border-[#d4af37]/15">
        <div className="max-w-6xl mx-auto">
          <p className="text-xs tracking-[0.25em] uppercase text-[#d4af37] mb-4 font-semibold">Credentials</p>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-[#fff8e1] mb-10">Academic & professional background</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {CREDENTIALS.map((c) => (
              <div key={c.label} data-testid={`credential-${c.label.slice(0, 12)}`} className="flex items-start gap-4 p-5 rounded-xl bg-[#11264c] border border-[#d4af37]/25 hover:border-[#d4af37]/60 transition-colors">
                <c.icon className="w-6 h-6 text-[#d4af37] shrink-0 mt-0.5" strokeWidth={1.5} />
                <span className="text-[#fff8e1]/90 font-medium leading-snug">{c.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-20 md:py-24 px-6 lg:px-10">
        <div className="max-w-4xl mx-auto space-y-8 text-lg text-[#fff8e1]/80 leading-relaxed">
          <div>
            <p className="text-xs tracking-[0.25em] uppercase text-[#d4af37] mb-4 font-semibold">Experience</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-[#fff8e1] mb-6">Seven+ years at an AACSB-accredited institution.</h2>
            <p>
              Dr. Baroody has more than seven years of full-time faculty experience at an AACSB-accredited institution, teaching undergraduate and graduate courses in <strong className="text-[#fff8e1]">Business IT Tools</strong>, <strong className="text-[#fff8e1]">Business Systems &amp; Processes</strong>, <strong className="text-[#fff8e1]">Excel for Business</strong>, and <strong className="text-[#fff8e1]">Project Management</strong>.
            </p>
          </div>

          <div>
            <p>
              Outside of the classroom, his work focuses on helping individuals and organizations turn technology, data, and business processes into practical tools for better decision-making, efficiency, and performance. He specializes in developing customized, analytics-driven, experiential training that connects directly to the work employees perform every day.
            </p>
          </div>

          <div>
            <h3 className="font-display text-xl font-bold text-[#fff8e1] mb-4">Areas of expertise</h3>
            <div className="flex flex-wrap gap-2">
              {EXPERTISE.map((e) => (
                <span key={e} className="inline-block px-3 py-1.5 rounded-full border border-[#d4af37]/40 text-sm text-[#fff8e1]/85 bg-[#11264c]">{e}</span>
              ))}
            </div>
          </div>

          <div className="pt-4">
            <p>
              At Central Florida Corporate Solutions, the goal is simple: provide practical, customized training that helps employees build usable skills, improve accuracy, work more efficiently, and deliver measurable value to their organizations — the way education was meant to work.
            </p>
            <p className="mt-6 text-[#d4af37] font-semibold italic">
              Dr. Baroody looks forward to meeting you in person.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 lg:px-10 bg-[#0a1830] border-t border-[#d4af37]/15">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-[#fff8e1] mb-5">Ready to bring practical training to your team?</h2>
          <p className="text-[#fff8e1]/70 text-lg mb-8">Book a 15-minute walkthrough — we&apos;ll come back with a tailored proposal within one business day.</p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <a href={CALENDLY_URL} target="_blank" rel="noreferrer" data-testid="about-cta-calendly" className="inline-flex items-center gap-2 bg-[#d4af37] text-[#0b1d3a] px-7 py-3.5 rounded-md font-semibold hover:bg-[#e2bd44] transition-all shadow-[0_8px_30px_rgba(212,175,55,0.25)]">
              Book a walkthrough <ArrowRight className="w-4 h-4" />
            </a>
            <Link to="/#contact" data-testid="about-cta-contact" className="inline-flex items-center gap-2 border-2 border-[#d4af37]/50 text-[#fff8e1] px-7 py-3.5 rounded-md font-semibold hover:border-[#d4af37] hover:bg-[#d4af37]/5 transition-colors">
              Send a message
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#d4af37]/20 py-10 px-6 lg:px-10 bg-[#0a1830]">
        <div className="max-w-6xl mx-auto text-center">
          <img src={LOGO_URL} alt="CFCS" className="h-9 w-auto rounded-sm mx-auto mb-3" />
          <p className="text-xs text-[#fff8e1]/40">© {new Date().getFullYear()} {BRAND.name}. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
